import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Globe, Building2, Medal } from 'lucide-react';
import { fetchLeaderboard, buildAvatarUrl } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/components/PageHeader';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './LeaderboardPage.module.css';

interface Props { onNavigate: (path: string) => void }

const TIER_COLORS: Record<string, string> = {
  bronze: '#cd7f32', silver: '#c0c0c0', gold: '#ffd700',
  platinum: '#e5e4e2', diamond: '#b9f2ff',
};

function medalColor(pos: number): string {
  if (pos === 1) return '#ffd700';
  if (pos === 2) return '#c0c0c0';
  if (pos === 3) return '#cd7f32';
  return 'var(--color-tx-m)';
}

export function LeaderboardPage({ onNavigate: _ }: Props) {
  const { t } = useTranslation('leaderboard');
  const [scope, setScope] = useState<'global' | 'tenant'>('global');
  const me = useAuthStore((s) => s.user);

  const lbQ = useQuery({
    queryKey: ['leaderboard', scope],
    queryFn: () => fetchLeaderboard(scope),
  });

  const entries = lbQ.data ?? [];
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <div className={styles.page}>
      <PageHeader title={t('title')} subtitle={t('subtitle')} icon={Trophy} />

      <div className={styles.tabs}>
        <button className={`${styles.tab} ${scope === 'global' ? styles.tabActive : ''}`} onClick={() => setScope('global')}>
          <Globe size={14} /> {t('tabs.global')}
        </button>
        <button className={`${styles.tab} ${scope === 'tenant' ? styles.tabActive : ''}`} onClick={() => setScope('tenant')}>
          <Building2 size={14} /> {t('tabs.network')}
        </button>
      </div>

      {lbQ.isLoading ? (
        <p className={styles.empty}>{t('loading')}</p>
      ) : entries.length === 0 ? (
        <div className={styles.empty}>
          <NamedIcon
            name="trophy"
            fallback={<Trophy size={32} className={styles.emptyIcon} />}
            size={32}
          />
          <p>{t('empty')}</p>
        </div>
      ) : (
        <>
          {/* Top 3 podium */}
          {top3.length === 3 && (
            <div className={styles.podium}>
              {[top3[1], top3[0], top3[2]].map((e, idx) => {
                if (!e) return null;
                const pos = idx === 1 ? 1 : (idx === 0 ? 2 : 3);
                const isMe = me?._id === e.user_id;
                return (
                  <div key={e.user_id} className={`${styles.podiumCard} ${styles[`p${pos}`]} ${isMe ? styles.podiumMe : ''}`}>
                    <Medal size={18} style={{ color: medalColor(pos) }} />
                    <div className={styles.podiumPos}>#{pos}</div>
                    <img
                      src={e.avatar_url ?? (e.avatar_style && e.avatar_seed ? buildAvatarUrl(e.avatar_style, e.avatar_seed) : buildAvatarUrl('avataaars-neutral', e.user_id))}
                      alt=""
                      className={styles.podiumAvatar}
                    />
                    <p className={styles.podiumName}>{e.username ?? t('driverFallback', { pos: e.position })}</p>
                    {e.current_rank && (
                      <p className={styles.podiumRank} style={{ color: TIER_COLORS[e.current_rank?.toLowerCase()] ?? 'var(--color-tx-m)' }}>
                        {e.current_rank}
                      </p>
                    )}
                    <p className={styles.podiumPts}>{e.points.toLocaleString()}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Rest */}
          <div className={styles.list}>
            {rest.map((e) => {
              const isMe = me?._id === e.user_id;
              return (
                <div key={e.user_id} className={`${styles.row} ${isMe ? styles.rowMe : ''}`}>
                  <span className={styles.rowPos}>#{e.position}</span>
                  <img
                    src={e.avatar_url ?? (e.avatar_style && e.avatar_seed ? buildAvatarUrl(e.avatar_style, e.avatar_seed) : buildAvatarUrl('avataaars-neutral', e.user_id))}
                    alt=""
                    className={styles.rowAvatar}
                  />
                  <div className={styles.rowInfo}>
                    <p className={styles.rowName}>{e.username ?? t('driverFallback', { pos: e.position })} {isMe && <span className={styles.youTag}>{t('you')}</span>}</p>
                    <p className={styles.rowMeta}>
                      {t('meta', { sessions: e.total_sessions, kwh: e.total_kwh.toFixed(0) })}
                    </p>
                  </div>
                  <p className={styles.rowPts}>{e.points.toLocaleString()}</p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
